import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/lib/database.types";

/** Digits only; E.164 without `+` (see product spec). Empty / missing → null. */
export function normalizeProfilePhoneDigits(
  phone: string | null | undefined,
): string | null {
  if (phone == null) return null;
  const digits = phone.replace(/\D/g, "");
  return digits.length === 0 ? null : digits;
}

/** Minimum bar for “has a plausible handset number” before enabling WhatsApp near-me. */
export function isPhoneValidForNearMeWhatsapp(digits: string | null): boolean {
  if (!digits) return false;
  return digits.length >= 10 && digits.length <= 15;
}

/**
 * Default seller availability WhatsApp pref at signup — valid phone only (not gated on listings).
 */
export function defaultSellerAvailabilityWhatsappEnabled(
  phoneDigits: string | null | undefined,
): boolean {
  const digits = normalizeProfilePhoneDigits(phoneDigits ?? null);
  return isPhoneValidForNearMeWhatsapp(digits);
}

export type NearMeWhatsappConsentSnapshot = {
  enabled: boolean;
  opt_in_at: string | null;
  opt_out_at: string | null;
};

export type NearMeWhatsappConsentPatch = {
  near_me_notify_whatsapp: boolean;
  near_me_whatsapp_opt_in_at: string | null;
  near_me_whatsapp_opt_out_at: string | null;
};

/**
 * Computes next DB values for WhatsApp near-me consent fields.
 * Preserves opt-in history; stamps opt-out when disabling or when phone is invalid while enabled.
 */
export function nextNearMeWhatsappConsentFields(args: {
  nowIso: string;
  previous: NearMeWhatsappConsentSnapshot;
  /** User intent: wants WhatsApp on (still subject to phone validity). */
  requestedEnabled: boolean;
  phoneDigits: string | null;
}): NearMeWhatsappConsentPatch {
  const phoneOk = isPhoneValidForNearMeWhatsapp(args.phoneDigits);
  const want = args.requestedEnabled && phoneOk;

  if (!phoneOk && args.previous.enabled) {
    return {
      near_me_notify_whatsapp: false,
      near_me_whatsapp_opt_in_at: args.previous.opt_in_at,
      near_me_whatsapp_opt_out_at: args.nowIso,
    };
  }
  if (!phoneOk) {
    return {
      near_me_notify_whatsapp: false,
      near_me_whatsapp_opt_in_at: args.previous.opt_in_at,
      near_me_whatsapp_opt_out_at: args.previous.opt_out_at,
    };
  }

  if (want && !args.previous.enabled) {
    return {
      near_me_notify_whatsapp: true,
      near_me_whatsapp_opt_in_at: args.nowIso,
      near_me_whatsapp_opt_out_at: null,
    };
  }
  if (!want && args.previous.enabled) {
    return {
      near_me_notify_whatsapp: false,
      near_me_whatsapp_opt_in_at: args.previous.opt_in_at,
      near_me_whatsapp_opt_out_at: args.nowIso,
    };
  }

  return {
    near_me_notify_whatsapp: args.previous.enabled,
    near_me_whatsapp_opt_in_at: args.previous.opt_in_at,
    near_me_whatsapp_opt_out_at: args.previous.opt_out_at,
  };
}

/**
 * When profile phone is missing or too short, turn off WhatsApp near-me if it was on.
 * Best-effort; logs errors without throwing.
 */
export async function disableNearMeWhatsappIfPhoneInvalid(
  client: SupabaseClient<Database>,
  userId: string,
  phoneDisplay: string | null | undefined,
): Promise<void> {
  const digits = normalizeProfilePhoneDigits(phoneDisplay);
  if (isPhoneValidForNearMeWhatsapp(digits)) return;

  const { data, error } = await client
    .from("notification_preferences")
    .select("near_me_notify_whatsapp")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    console.error("[near_me_whatsapp] prefs read after phone change:", error);
    return;
  }
  if (!data?.near_me_notify_whatsapp) return;

  const nowIso = new Date().toISOString();
  const { error: upErr } = await client
    .from("notification_preferences")
    .update({
      near_me_notify_whatsapp: false,
      near_me_whatsapp_opt_out_at: nowIso,
      updated_at: nowIso,
    })
    .eq("user_id", userId);

  if (upErr) {
    console.error(
      "[near_me_whatsapp] prefs disable after phone change:",
      upErr,
    );
  }
}

/**
 * When profile phone is missing or too short, turn off seller availability WhatsApp opt-in.
 */
export async function disableSellerAvailabilityWhatsappIfPhoneInvalid(
  client: SupabaseClient<Database>,
  userId: string,
  phoneDisplay: string | null | undefined,
): Promise<void> {
  const digits = normalizeProfilePhoneDigits(phoneDisplay);
  if (isPhoneValidForNearMeWhatsapp(digits)) return;

  const { data, error } = await client
    .from("notification_preferences")
    .select("notify_seller_availability_whatsapp")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    console.error(
      "[seller_availability_whatsapp] prefs read after phone change:",
      error,
    );
    return;
  }
  if (!data?.notify_seller_availability_whatsapp) return;

  const nowIso = new Date().toISOString();
  const { error: upErr } = await client
    .from("notification_preferences")
    .update({
      notify_seller_availability_whatsapp: false,
      updated_at: nowIso,
    })
    .eq("user_id", userId);

  if (upErr) {
    console.error(
      "[seller_availability_whatsapp] prefs disable after phone change:",
      upErr,
    );
  }
}

/**
 * On profile phone change: disable seller WA when phone invalid; default-on when user
 * gains a valid phone for the first time (invalid/missing → valid). Does not re-enable
 * when replacing one valid number with another (explicit opt-out preserved).
 */
export async function syncSellerAvailabilityWhatsappForPhoneChange(
  client: SupabaseClient<Database>,
  userId: string,
  previousPhoneDisplay: string | null | undefined,
  nextPhoneDisplay: string | null | undefined,
): Promise<void> {
  const prevDigits = normalizeProfilePhoneDigits(previousPhoneDisplay);
  const nextDigits = normalizeProfilePhoneDigits(nextPhoneDisplay);
  const prevOk = isPhoneValidForNearMeWhatsapp(prevDigits);
  const nextOk = isPhoneValidForNearMeWhatsapp(nextDigits);

  if (!nextOk) {
    await disableSellerAvailabilityWhatsappIfPhoneInvalid(
      client,
      userId,
      nextPhoneDisplay,
    );
    return;
  }

  if (prevOk) return;

  const { data, error } = await client
    .from("notification_preferences")
    .select("notify_seller_availability_whatsapp")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    console.error(
      "[seller_availability_whatsapp] prefs read after phone change:",
      error,
    );
    return;
  }
  if (data?.notify_seller_availability_whatsapp === true) return;

  const nowIso = new Date().toISOString();
  const { error: upErr } = await client
    .from("notification_preferences")
    .update({
      notify_seller_availability_whatsapp: true,
      updated_at: nowIso,
    })
    .eq("user_id", userId);

  if (upErr) {
    console.error(
      "[seller_availability_whatsapp] prefs enable after first valid phone:",
      upErr,
    );
  }
}
